import React from 'react';

interface GoogleMapProps {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
  height?: string;
  zoom?: number;
  className?: string;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ 
  latitude, 
  longitude, 
  name = 'Location',
  address = '',
  height = 'h-64 md:h-80',
  zoom = 15,
  className = ''
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyA6njPFHRLiKVAn7_6bWt1d_8sseEcv_Gg';
  
  // Use place name for better marker display, fallback to coordinates
  const query = address 
    ? encodeURIComponent(`${name}, ${address}`)
    : `${latitude},${longitude}`;
  
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}&zoom=${zoom}&maptype=roadmap&language=en`;

  const generateDirectionsUrl = () => {
    return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
  };

  const generateSearchUrl = () => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || `${name} location`)}`;
  };

  return (
    <div className={`bg-white rounded-2xl border border-orange-200/50 shadow-lg overflow-hidden ${className}`}>
      {/* Map Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          🗺️ Location Map
        </h3>
        {address && (
          <p className="text-sm text-gray-600 mt-1">{address}</p>
        )}
      </div>

      {/* Map Display */}
      <div className={`relative ${height}`}>
        <iframe
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src={mapUrl}
          title={`${name} Location`}
        />
      </div>

      {/* Map Actions */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap gap-3">
          <a
            href={generateDirectionsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            🧭 Get Directions
          </a>
          <a
            href={generateSearchUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium"
          >
            🌍 View on Google Maps
          </a>
          <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-800 rounded-lg text-sm">
            📍 {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMap;
